import { useMemo, useState } from 'react'
import {
  Modal, Stepper, Select, TextInput, Textarea, Button, Group, Stack,
  Table, Alert, Text, Radio,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'
import { listAttachableAlbs, provisionWaf, type AttachableAlb,
  type ProvisionWafResponse } from '@/api/waf'
import { getChangeControlPreview } from '@/api/changeControl'
import { PROXIMO_PASSO_GMUD } from '@/components/ZtnaAdmin/gmudNotice'

const REGIONS = ['us-east-1', 'us-east-2', 'us-west-2', 'sa-east-1', 'eu-west-1']

interface TenantRow { id: number; name: string; account_id: string | null }

function sanitizeName(s: string): string {
  return ('consoleai-' + (s || 'alb')).replace(/[^A-Za-z0-9-_]/g, '-').slice(0, 128)
}

interface Props {
  opened: boolean
  onClose: () => void
  /**
   * Entrega o resultado do POST ao chamador, para ele abrir o modal de progresso.
   *
   * O wizard NÃO abre o modal por conta própria: quem sabe onde ele deve viver na
   * árvore é a página, e acoplar um modal de acompanhamento dentro do wizard faria o
   * modal morrer junto com ele ao fechar.
   */
  onProvisioned?: (r: ProvisionWafResponse & {
    tenant_id: number; desired_name: string; alb_name: string | null
  }) => void
}

/**
 * Wizard de provisionamento de WAF (#3): DevOps cria um WebACL novo (baseline AWS
 * managed em Count) e o anexa a um ALB de tenant sem WAF.
 *
 * **Nem todo tenant passa por GMUD.** Desde 08/08/2026 o comitê é o interruptor
 * do controle de mudança: sem comitê ativo na organização, ou fora de produção,
 * o provisionamento acontece direto. O wizard pergunta ao gate assim que o
 * tenant é escolhido e rotula o botão com o que vai de fato acontecer — antes
 * ele prometia "Enviar para GMUD" a todo mundo, e mandava o operador esperar
 * por uma aprovação que nunca vinha.
 */
export function WafProvisionWizard({ opened, onClose, onProvisioned }: Props) {
  const [active, setActive] = useState(0)
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [accountId, setAccountId] = useState('')
  const [region, setRegion] = useState('us-east-1')
  const [albs, setAlbs] = useState<AttachableAlb[]>([])
  const [selectedArn, setSelectedArn] = useState<string | null>(null)
  const [desiredName, setDesiredName] = useState('')
  // O nome sugerido acompanha o ALB selecionado ENQUANTO ninguém o editou à
  // mão. Sem esta marca, trocar de ALB deixava o nome do primeiro: o pedido
  // nascia `consoleai-ALB-internal-X` apontando para o `ALB-public-X`.
  const [nameTouched, setNameTouched] = useState(false)
  const [reason, setReason] = useState('')

  const tenants = useQuery({
    queryKey: ['admin-tenants-min'],
    queryFn: () => api.get<{ items: TenantRow[] }>('/v1/admin/tenants').then(r => r.data.items ?? []),
    enabled: opened,
  })

  // Prévia do gate: só muda o que a tela PROMETE, nunca o que ela envia. Se a
  // consulta falhar ou ainda estiver voando, o texto cai no neutro — anunciar
  // GMUD por engano é pior que não anunciar nada.
  const gate = useQuery({
    queryKey: ['change-control-preview', 'waf_provision', tenantId],
    queryFn: () => getChangeControlPreview(Number(tenantId), 'waf_provision'),
    enabled: opened && !!tenantId,
    staleTime: 30_000,
  })
  const exigeGmud = gate.data?.requires_gmud
  const submitLabel = exigeGmud === true
    ? 'Enviar para GMUD'
    : exigeGmud === false ? 'Provisionar agora' : 'Enviar pedido'

  const listMut = useMutation({
    mutationFn: () => listAttachableAlbs(accountId, region),
    onSuccess: (data) => { setAlbs(data.albs); setActive(1) },
    onError: (e) => notifications.show({
      color: 'red', title: 'Listar ALBs', message: formatApiError(e, 'Falha ao listar ALBs.'),
    }),
  })

  const submitMut = useMutation({
    mutationFn: () => provisionWaf({
      tenant_id: Number(tenantId), account_id: accountId, region,
      alb_arn: selectedArn as string,
      alb_name: albs.find(a => a.arn === selectedArn)?.name ?? null,
      desired_name: desiredName, reason: reason || null,
    }),
    onSuccess: (data) => {
      // Sem GMUD o provisionamento segue direto — anunciar "aguardando
      // aprovação (#undefined)" mandaria o operador esperar por algo que nunca
      // vem, enquanto o WAF sobe em instantes.
      notifications.show(data.gmud_id != null ? {
        color: 'green', title: 'Provisionamento enviado',
        message: `Aguardando aprovação GMUD (#${data.gmud_id}). O WAF aparece no registro após a aprovação. ${PROXIMO_PASSO_GMUD}`,
      } : {
        color: 'green', title: 'Provisionamento em andamento',
        message: 'Esta organização não passa por GMUD, então o WAF está sendo criado agora e aparece no registro em instantes.',
      })
      // O TENANT DO PEDIDO, nao o da pagina: este wizard tem seletor proprio, e
      // provisionar para `rota-dev` com a pagina em `commerce-hml` fazia o modal pedir
      // um id que a rota por tenant nao acha — `pedido_nao_encontrado`. Achado no aceite
      // de 14/08, e e o tipo de defeito que so aparece quando os dois divergem.
      onProvisioned?.({
        ...data,
        tenant_id: Number(tenantId),
        desired_name: desiredName,
        alb_name: albs.find(a => a.arn === selectedArn)?.name ?? null,
      })
      handleClose()
    },
    onError: (e) => notifications.show({
      color: 'red', title: 'Provisionar WAF', message: formatApiError(e, 'Falha ao enviar.'),
    }),
  })

  const selectedAlb = useMemo(
    () => albs.find(a => a.arn === selectedArn) ?? null, [albs, selectedArn],
  )

  // A conta AWS é derivada do tenant selecionado (tenants.account_id) — o operador
  // não digita, só vê. Fallback editável se o tenant não tiver conta vinculada.
  const selectedTenant = useMemo(
    () => (tenants.data ?? []).find(t => String(t.id) === tenantId) ?? null,
    [tenants.data, tenantId],
  )
  const accountAuto = !!selectedTenant?.account_id

  function handleTenantChange(v: string | null) {
    setTenantId(v)
    const t = (tenants.data ?? []).find(tr => String(tr.id) === v)
    setAccountId(t?.account_id ?? '')
  }

  function handleClose() {
    setActive(0); setTenantId(null); setAccountId(''); setRegion('us-east-1')
    setAlbs([]); setSelectedArn(null); setDesiredName(''); setReason('')
    setNameTouched(false)
    onClose()
  }

  function selectAlb(a: AttachableAlb) {
    if (a.has_waf) return
    setSelectedArn(a.arn)
    if (!nameTouched) setDesiredName(sanitizeName(a.name))
  }

  const canList = !!tenantId && /^\d{12}$/.test(accountId) && !!region
  const canSubmit = !!selectedArn && /^[A-Za-z0-9-_]{1,128}$/.test(desiredName)

  return (
    <Modal opened={opened} onClose={handleClose} title="Provisionar WAF" size="xl">
      <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
        <Stepper.Step label="Alvo" description="Tenant, conta e região">
          <Stack gap="sm" mt="md">
            {/* A listagem caindo virava `[]`: o Select ficava vazio, sem placeholder de erro e
              sem nada para escolher — indistinguivel de uma instalacao sem tenant nenhum. Aqui
              o afordamento certo e o `error` do proprio campo, e nao um bloco no lugar dele. */}
            <Select
              label="Tenant" placeholder="Selecione o tenant" searchable
              data={(tenants.data ?? []).map(t => ({ value: String(t.id), label: `${t.name} (#${t.id})` }))}
              value={tenantId} onChange={handleTenantChange}
              error={tenants.isError ? 'Não foi possível carregar os tenants. Feche e abra de novo.' : undefined}
            />
            <TextInput
              label="Conta AWS"
              description={
                !tenantId
                  ? 'Selecione um tenant — a conta é preenchida automaticamente.'
                  : accountAuto
                    ? 'Preenchida automaticamente pelo tenant selecionado.'
                    : 'Tenant sem conta vinculada — informe o ID de 12 dígitos.'
              }
              placeholder="111122223333"
              value={accountId} onChange={e => setAccountId(e.currentTarget.value.trim())}
              disabled={accountAuto}
              error={accountId && !/^\d{12}$/.test(accountId) ? 'ID de conta inválido' : undefined}
            />
            <Select label="Região" data={REGIONS} value={region} onChange={v => setRegion(v ?? 'us-east-1')} />
            <Group justify="flex-end">
              <Button disabled={!canList} loading={listMut.isPending} onClick={() => listMut.mutate()}>
                Buscar ALBs
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>

        <Stepper.Step label="ALB + confirmação" description="Escolher o alvo e enviar">
          <Stack gap="sm" mt="md">
            <Text size="sm" c="dimmed">
              O Web ACL nasce com o baseline AWS gerenciado (CommonRuleSet + KnownBadInputs)
              em modo Count (observação), que só bloqueia depois que você promover a regra.
              Bloqueiam desde o primeiro dia só a lista de bloqueio (nasce vazia) e o
              Block-phpinfo, que responde 404 a /info.php e /phpinfo.php (inclusive com caminho depois).
            </Text>
            {albs.length === 0 ? (
              <Alert color="gray">Nenhum ALB encontrado nesta conta/região.</Alert>
            ) : (
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>Nome</Table.Th>
                    <Table.Th>DNS</Table.Th>
                    <Table.Th>Esquema</Table.Th>
                    <Table.Th>WAF</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {albs.map(a => (
                    <Table.Tr
                      key={a.arn}
                      style={{ opacity: a.has_waf ? 0.5 : 1, cursor: a.has_waf ? 'not-allowed' : 'pointer' }}
                      onClick={() => selectAlb(a)}
                    >
                      <Table.Td><Radio checked={selectedArn === a.arn} disabled={a.has_waf} readOnly /></Table.Td>
                      <Table.Td>{a.name}</Table.Td>
                      <Table.Td><Text size="xs" ff="monospace" c="dimmed">{a.dns_name}</Text></Table.Td>
                      <Table.Td>{a.scheme}</Table.Td>
                      <Table.Td>{a.has_waf ? `já protegido: ${a.web_acl_name ?? 'sim'}` : '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {selectedAlb?.scheme === 'internet-facing' && (
              <Alert color="yellow" title="ALB internet-facing">
                Este ALB pode servir vários aplicativos. O WAF é associado ao ALB inteiro —
                todos os apps atrás dele passam a ser avaliados.
                {exigeGmud === true
                  ? ' A GMUD cobre a decisão.'
                  : exigeGmud === false
                    ? ' Esta organização não passa por GMUD: a decisão é sua, e o WAF sobe assim que você enviar.'
                    : ''}
              </Alert>
            )}
            <TextInput
              label="Nome do Web ACL" description="[A-Za-z0-9-_], até 128 caracteres"
              value={desiredName}
              onChange={e => { setNameTouched(true); setDesiredName(e.currentTarget.value) }}
              error={desiredName && !/^[A-Za-z0-9-_]{1,128}$/.test(desiredName) ? 'Nome inválido' : undefined}
            />
            <Textarea
              label="Justificativa (opcional)" value={reason}
              onChange={e => setReason(e.currentTarget.value)} autosize minRows={2}
            />
            <Group justify="space-between">
              <Button variant="default" onClick={() => setActive(0)}>Voltar</Button>
              <Group gap="sm">
                {exigeGmud === false && (
                  <Text size="xs" c="dimmed">
                    Sem GMUD ({gate.data?.motivo}) — o WAF é criado em seguida.
                  </Text>
                )}
                <Button disabled={!canSubmit} loading={submitMut.isPending} onClick={() => submitMut.mutate()}>
                  {submitLabel}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Stepper.Step>
      </Stepper>
    </Modal>
  )
}
